/**
 * PDF 업로드 — 쪽마다 그림으로 바꿔 올린다(2026-10-28).
 *
 * 화면(에이전트)은 PDF 를 틀지 못한다. 안드로이드 5.1 웹뷰·Google TV·Apple TV 어느 쪽에도 PDF 를 그리는 길이 없거나 제각각이다.
 * 그래서 올리는 자리에서 **쪽 그림(PNG·JPEG)으로 바꿔** 보통 그림 콘텐츠로 올리고, 여러 쪽이면 쪽 순서대로 담은 플레이리스트를
 * 만든다(`Content.tsx` 의 업로더). 화면은 늘 그리던 그림을 받을 뿐이라 기기·에이전트 판과 상관없이 나간다.
 *
 * **콘솔(브라우저)에서 바꾼다.** 서버에서 그리려면 캔버스 네이티브 모듈이 필요한데, 서버 패키지는 네이티브 모듈을 싣지 않는다
 * (다른 CPU 에서 조용히 죽는다 — `scripts/package-server.mjs`). 브라우저에는 캔버스와 글꼴이 이미 있고, 설명서 내보내기
 * (`manual/export/pptx.ts`)가 같은 pdfjs 로 쪽을 굽고 있다. API 로 PDF 를 올리면 서버는 그림으로 바꿔 올리라고 답한다.
 *
 * 글꼴을 싣지 않은 한글 PDF 를 그리는 문자표·표준 글꼴·이미지 해독기는 콘솔 빌드의 `pdfjs/` 에 있다(`vite.config.ts`).
 */
import workerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url';

/**
 * 쪽 그림의 긴 변(px). 사이니지 화면 1920×1080 을 가로로 세우든 세로로 세우든 **줄이기만 하면 되는** 크기다 — 세로 A4 는
 * 1358×1920 이 되어 세로 화면을 꽉 채우고, 가로 화면에서는 높이 1080 으로 줄어 들어간다.
 */
export const PAGE_LONG_EDGE = 1920;

/** 한 PDF 에서 바꾸는 쪽 수의 한도. 사이니지로 넘겨 보일 문서치고 많고, 브라우저가 한 번에 굽기에 무리 없는 수. */
export const PDF_MAX_PAGES = 100;

/**
 * PNG 로 둘 크기의 한도. 글자 쪽은 PNG 가 작고 또렷하다(수백 KB). 사진이 깔린 쪽은 PNG 가 수 MB 로 커지므로 그때만 JPEG 로
 * 바꾼다 — 화면이 쪽마다 받는 파일이고, 5.1 기기는 저장 공간이 좁다.
 */
const PNG_MAX_BYTES = 1024 * 1024;
const JPEG_QUALITY = 0.9;

export interface PdfPage {
  /** 올릴 파일 — `안전수칙-01.png` 처럼 PDF 이름과 쪽 번호. */
  file: File;
  /** 콘텐츠 제목 — `안전수칙.pdf (1/12)`. 한 쪽짜리면 PDF 파일 이름 그대로. */
  title: string;
}

export function isPdfFile(file: Pick<File, 'name' | 'type'>): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

/** 확장자를 뗀 PDF 이름 — 쪽 파일 이름과 플레이리스트 이름에 쓴다. */
export function pdfBaseName(fileName: string): string {
  return fileName.replace(/\.pdf$/i, '').trim() || 'PDF';
}

/** 쪽(pt 단위 크기)을 긴 변 `PAGE_LONG_EDGE` 로 맞추는 배율. */
export function pageScale(width: number, height: number): number {
  return PAGE_LONG_EDGE / Math.max(width, height, 1);
}

export function pageTitle(fileName: string, page: number, total: number): string {
  return total === 1 ? fileName : `${fileName} (${page}/${total})`;
}

/** 쪽 파일 이름 — 쪽 번호를 자릿수에 맞춰 채워 이름순으로도 쪽 순서가 된다. */
export function pageFileName(fileName: string, page: number, total: number, extension: 'png' | 'jpg'): string {
  const base = pdfBaseName(fileName);
  if (total === 1) return `${base}.${extension}`;
  return `${base}-${String(page).padStart(String(total).length, '0')}.${extension}`;
}

/** 사람이 읽을 말로 — 업로드 결과에 파일 이름과 함께 그대로 나간다. */
export function pdfFailureText(error: unknown): string {
  const name = error instanceof Error ? error.name : '';
  if (name === 'PasswordException') return '암호가 걸린 PDF 입니다 — 암호를 풀어 저장한 뒤 다시 올리세요';
  if (name === 'InvalidPDFException') return 'PDF 를 읽지 못했습니다 — 파일이 손상되었거나 PDF 가 아닙니다';
  return error instanceof Error && error.message ? error.message : 'PDF 를 그림으로 바꾸지 못했습니다';
}

/** pdfjs 가 찾아 읽는 부속 파일 자리 — 콘솔이 하위 경로·임베드(상대 경로)로 서빙돼도 맞게 절대 주소로 푼다. */
function dataUrl(dir: string): string {
  return new URL(`${import.meta.env.BASE_URL}pdfjs/${dir}/`, document.baseURI).href;
}

function canvasBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('쪽 그림을 만들지 못했습니다'))), type, quality);
  });
}

/**
 * PDF 를 쪽 그림으로. 쪽을 하나 구울 때마다 `onPage(구운 쪽, 전체 쪽)` 을 부른다.
 *
 * 실패는 사람이 읽을 말로 던진다(`pdfFailureText`) — 암호·손상·쪽 수 한도.
 */
export async function pdfToPages(file: File, onPage?: (done: number, total: number) => void): Promise<PdfPage[]> {
  const pdfjs = await import('pdfjs-dist');
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  const task = pdfjs.getDocument({
    data: new Uint8Array(await file.arrayBuffer()),
    cMapUrl: dataUrl('cmaps'),
    cMapPacked: true,
    standardFontDataUrl: dataUrl('standard_fonts'),
    wasmUrl: dataUrl('wasm'),
    iccUrl: dataUrl('iccs'),
  });
  /*
   * 캔버스는 한 장만 만들어 다시 쓴다 — 쪽마다 새로 만들면 1920px 그림판이 쪽 수만큼 쌓여 탭이 메모리로 죽는다
   * (설명서 내보내기에서 실제로 그랬다).
   */
  const canvas = document.createElement('canvas');
  try {
    const pdf = await task.promise.catch((error: unknown) => {
      throw new Error(pdfFailureText(error));
    });
    const total = pdf.numPages;
    if (total === 0) throw new Error('쪽이 없는 PDF 입니다');
    if (total > PDF_MAX_PAGES) {
      throw new Error(
        `쪽이 너무 많습니다(${total}쪽) — 한 번에 ${PDF_MAX_PAGES}쪽까지 바꿉니다. 필요한 쪽만 나눠 저장해 올리세요`,
      );
    }

    const pages: PdfPage[] = [];
    for (let number = 1; number <= total; number += 1) {
      const page = await pdf.getPage(number);
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: pageScale(base.width, base.height) });
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);
      const context = canvas.getContext('2d');
      if (!context) throw new Error('쪽을 그릴 캔버스를 열지 못했습니다');
      // 투명한 자리는 종이처럼 흰색으로 — 화면 바탕(검정)이 비쳐 글자가 묻히지 않게.
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvas, canvasContext: context, viewport }).promise;
      page.cleanup();

      const png = await canvasBlob(canvas, 'image/png');
      let blob = png;
      if (png.size > PNG_MAX_BYTES) {
        const jpeg = await canvasBlob(canvas, 'image/jpeg', JPEG_QUALITY);
        if (jpeg.size < png.size) blob = jpeg;
      }
      const extension = blob.type === 'image/jpeg' ? 'jpg' : 'png';
      pages.push({
        file: new File([blob], pageFileName(file.name, number, total, extension), { type: blob.type }),
        title: pageTitle(file.name, number, total),
      });
      onPage?.(number, total);
    }
    return pages;
  } finally {
    canvas.width = 0;
    canvas.height = 0;
    await task.destroy();
  }
}
